// PlateModal.tsx
import { useEffect, useRef, useState } from "react";
import { FaAngleLeft } from "react-icons/fa";
import { Portal } from "../../portal/Portal";
import { ComboSelector } from "../../cards/ComboSelector";
import { usePlateModal } from "../../../../hooks/usePlateModal";
import styles from "./PlateModal.module.scss";
import { useOrdersStore, useUIStore } from "../../../../store";
import { getAssetUrl } from "../../../../utils/getAssetUrl";
import { formatPrice } from "../../../../utils/formatersPrecio";
import { PricePlate, ProductImage } from "../../../atoms";
import type { OptionGroup } from "../../../../interfaces/domain/menu";

function groupTypeLabel(group: OptionGroup, showQty: boolean): string {
  const { min_selection: min, max_selection: max } = group;
  if (min === 0) return "EXTRAS";
  if (!showQty) return "SELECCIÓN";
  if (min === max) return `SELECCIÓN · ${min} ${min === 1 ? "unidad" : "unidades"}`;
  return `SELECCIÓN · ${min} a ${max} unidades`;
}

export const PlateModal = ({
  plate,
  onClose,
  cartItem,
  menuData,
  visible,
}: any) => {
  const {
    quantity,
    setQuantity,
    selection,
    setSelection,
    optionSelection,
    setOptionSelection,
    toggleCheckItem,
    setOptionItemQty,
    isOptionsIncomplete,
    totalFaltanOptions,
    totalPrice,
    isClosing,
    setIsClosing,
    setIsLoading,
    handleStartClosing,
    handleSubmit,
    isCombo,
    isLoading,
    buttonText,
    plateCategory,
  } = usePlateModal(plate, onClose, cartItem, menuData);

  const [showStickyHeader, setShowStickyHeader] = useState(false);

  const config = useUIStore((s) => s.config);
  const canUseCart = useOrdersStore((s) => s.canUseCart);
  const isLocalCombo = isCombo && !canUseCart;
  const src = getAssetUrl(plate?.image_url, config);

  useEffect(() => {
    if (visible) {
      setIsClosing(false);
      setIsLoading(false);
      setShowStickyHeader(false);
      setQuantity(cartItem?.quantity ?? 1);
      if (imageParallaxRef.current) imageParallaxRef.current.style.transform = "";
      if (contentRef.current) contentRef.current.scrollTop = 0;

      if (cartItem?.comboSelection) {
        setSelection(
          Object.fromEntries(cartItem.comboSelection.map((i: any) => [i.id, i.quantity]))
        );
      } else {
        setSelection({});
      }
      setOptionSelection(cartItem?.optionSelection ?? {});

      document.body.classList.add("modal-open");
      if (window.innerWidth >= 1024) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [visible]);

  // Scroll solo hacia abajo en el contenido
  const contentRef = useRef<HTMLDivElement>(null);
  const imageParallaxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      const dy = e.touches[0].clientY - startY;
      if (dy > 0 && el.scrollTop === 0) {
        e.preventDefault();
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  // Parallax: imagen se mueve a 0.5× la velocidad del scroll (solo mobile)
  useEffect(() => {
    if (!src) return;
    const el = contentRef.current;
    const img = imageParallaxRef.current;
    if (!el || !img) return;
    const onScroll = () => {
      if (window.innerWidth >= 1024) return;
      img.style.transform = `translateY(${-el.scrollTop * 0.5}px)`;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [src]);

  useEffect(() => {
    if (!visible) return;
    let cleanup: (() => void) | undefined;
    const timer = setTimeout(() => {
      const el = contentRef.current;
      if (!el) return;
      const onScroll = () => {
        const imageEl = el.querySelector('[class*="imageWrapperOuter"]') as HTMLElement | null;
        const threshold = imageEl ? Math.max(0, imageEl.offsetHeight - 52) : 0;
        setShowStickyHeader(el.scrollTop > threshold);
      };
      el.addEventListener("scroll", onScroll, { passive: true });
      cleanup = () => el.removeEventListener("scroll", onScroll);
    }, 50);
    return () => {
      clearTimeout(timer);
      cleanup?.();
    };
  }, [visible]);

  const isSelectionIncomplete =
    !isLocalCombo && isCombo &&
    (() => {
      const selectItem = plate?.combo?.items?.find(
        (i: any) => i.type?.toLowerCase() === "select"
      );
      if (!selectItem) return false;
      const totalSelected = Object.values(selection || {}).reduce(
        (acc: number, val: any) => acc + Number(val), 0
      );
      return totalSelected < selectItem.quantity;
    })();

  const selectRadioItem = (groupId: string, itemId: string) => {
    setOptionSelection({ ...optionSelection, [groupId]: { [itemId]: 1 } });
  };

  return (
    <Portal wrapperId="plate-modal-portal">
      <div className={visible ? undefined : styles.modalHidden}>
        <div className={`${styles.modalOverlay} ${isClosing ? styles.modalOverlayClosing : ""}`} onClick={handleStartClosing}>
          <div className={`${styles.modalWrapper} ${isClosing ? styles.modalWrapperClosing : ""}`} onClick={(e) => e.stopPropagation()}>
            <div
              className={`${styles.stickyHeader} ${showStickyHeader ? styles.stickyHeaderVisible : styles.stickyHeaderHidden}`}
            >
              <button type="button" className={styles.stickyHeaderBack} onClick={handleStartClosing}>
                <span className={styles.stickyHeaderBackInner}>
                  <FaAngleLeft size={24} />
                </span>
              </button>
              <span className={styles.stickyHeaderTitle}>{plate?.title}</span>
              <div className={styles.stickyHeaderSpacer} />
            </div>
            <div
              ref={contentRef}
              className={isClosing ? styles.modalContentClosing : styles.modalContent}
            >

            {src && (
              <div className={styles.imageWrapperOuter}>
                <div ref={imageParallaxRef} className={styles.imageParallaxInner}>
                  <ProductImage
                    src={src}
                    alt={plate?.title ?? ""}
                    showPlaceholder={false}
                    config={config}
                    wrapperClassName={styles.imageWrapperInner}
                    loading="eager"
                  />
                </div>
              </div>
            )}

            <div
              className={
                src
                  ? styles.containerTitleDescriptionPriceWithImage
                  : styles.containerTitleDescriptionPriceWithOutImage
              }
            >
              <div className={styles.containerTitleDescription}>
                {plateCategory && (
                  <div className={styles.categoryBadge}>
                    <span className={styles.categoryName}>{plateCategory.name}</span>
                    {plateCategory.description && (
                      <p className={styles.categoryDescription}>{plateCategory.description}</p>
                    )}
                  </div>
                )}
                <h2 className={styles.title}>{plate?.title}</h2>
                <p className={styles.fullDescription}>
                  {plate?.description?.trim() || plate?.title}
                </p>
                <PricePlate plate={plate} priceClassName={styles.bigPrice} />
              </div>

              {(plate?.options ?? []).length > 0 && (
                <div className={styles.optionGroups}>
                  {[...(plate.options as OptionGroup[])]
                .sort((a, b) => (a.min_selection === 0 ? 0 : 1) - (b.min_selection === 0 ? 0 : 1))
                .map((group) => {
                    const groupQtys = optionSelection[group.id] ?? {};
                    const totalSelected = Object.values(groupQtys).reduce((a, b) => a + b, 0);
                    const isSelection = group.min_selection > 0;
                    return (
                      <div key={group.id} className={styles.optionGroup}>
                        <div className={styles.optionGroupHeader}>
                          <div className={styles.optionGroupMeta}>
                            <span className={styles.optionGroupTypeLabel}>{groupTypeLabel(group, canUseCart)}</span>
                            <span className={styles.optionGroupName}>{group.name}</span>
                          </div>
                        </div>

                        {canUseCart && group.min_selection > 0 && (
                          <div className={`${styles.optionFeedback} ${
                            totalSelected < group.min_selection ? styles.optionPending : styles.optionComplete
                          }`}>
                            {totalSelected < group.min_selection
                              ? `Faltan elegir ${group.min_selection - totalSelected}`
                              : totalSelected < group.max_selection
                              ? `Mínimo alcanzado · podés agregar ${group.max_selection - totalSelected} más`
                              : "Máximo alcanzado ✓"}
                          </div>
                        )}

                        {!canUseCart ? (
                          <div className={styles.optionItems}>
                            {group.items.map((item) => (
                              <span key={item.id} className={styles.optionItem}>
                                <span className={styles.optionItemName}>{item.name}</span>
                                {item.price > 0 && <span className={styles.optionItemPrice}>+{formatPrice(item.price)}</span>}
                              </span>
                            ))}
                          </div>
                        ) : isSelection && group.max_selection === 1 ? (
                          <div className={styles.optionRadioList}>
                            {group.items.map((item) => {
                              const selected = (groupQtys[item.id] ?? 0) > 0;
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  className={`${styles.optionRadioRow} ${selected ? styles.optionRadioRowSelected : ""}`}
                                  onClick={() => selectRadioItem(group.id, item.id)}
                                >
                                  <div className={styles.optionRadioLabel}>
                                    <span className={styles.optionItemName}>{item.name}</span>
                                    {item.price > 0 && <span className={styles.optionItemPrice}>+{formatPrice(item.price)}</span>}
                                  </div>
                                  <span className={`${styles.optionRadioCircle} ${selected ? styles.optionRadioCircleSelected : ""}`} />
                                </button>
                              );
                            })}
                          </div>
                        ) : isSelection ? (
                          <div className={styles.optionItemsList}>
                            {group.items.map((item) => {
                              const qty = groupQtys[item.id] ?? 0;
                              return (
                                <div key={item.id} className={styles.optionItemRow}>
                                  <div className={styles.optionItemRowInfo}>
                                    <span className={styles.optionItemName}>{item.name}</span>
                                    {item.price > 0 && <span className={styles.optionItemPrice}>+{formatPrice(item.price)}</span>}
                                  </div>
                                  <div className={styles.optionQtyWidget}>
                                    <button
                                      type="button"
                                      className={styles.optionQtyBtn}
                                      onClick={() => setOptionItemQty(group.id, item.id, qty - 1, group.max_selection)}
                                      disabled={qty === 0}
                                    >−</button>
                                    <span className={styles.optionQtyValue}>{qty}</span>
                                    <button
                                      type="button"
                                      className={styles.optionQtyBtn}
                                      onClick={() => setOptionItemQty(group.id, item.id, qty + 1, group.max_selection)}
                                      disabled={totalSelected >= group.max_selection}
                                    >+</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className={styles.optionItems}>
                            {group.items.map((item) => {
                              const selected = !!(groupQtys[item.id]);
                              const disableAdd = totalSelected >= group.max_selection && !selected;
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  className={`${styles.optionItem} ${selected ? styles.optionItemSelected : ""}`}
                                  onClick={() => toggleCheckItem(group.id, item.id)}
                                  disabled={disableAdd}
                                  style={disableAdd ? { opacity: 0.5 } : undefined}
                                >
                                  <span className={styles.optionItemName}>{item.name}</span>
                                  {item.price > 0 && <span className={styles.optionItemPrice}>+{formatPrice(item.price)}</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {isCombo && (
                <ComboSelector
                  combo={plate?.combo}
                  quantity={quantity}
                  selection={selection}
                  setSelection={setSelection}
                  readOnly={isLocalCombo}
                />
              )}
            </div>

            {canUseCart && !isLocalCombo && (
              <div className={styles.containerBottomBar}>
                {!(plate?.options && plate.options.length > 0) && (
                  <div className={styles.containerQuantityModal}>
                    <button
                      type="button"
                      className={styles.qtyBtnModal}
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                    >−</button>
                    <span className={styles.qtyValueModal}>{quantity}</span>
                    <button
                      type="button"
                      className={styles.qtyBtnModal}
                      onClick={() => setQuantity((q) => q + 1)}
                    >+</button>
                  </div>
                )}
                <div className={styles.addButton}>
                  <button
                    className={`${styles.buttonRequest} ${(isSelectionIncomplete || isOptionsIncomplete) ? styles.buttonDisabled : ""}`}
                    onClick={handleSubmit}
                    disabled={isLoading || isSelectionIncomplete || isOptionsIncomplete}
                  >
                    {isSelectionIncomplete
                      ? "Falta completar selección"
                      : isOptionsIncomplete && totalPrice === 0
                      ? `Faltan elegir ${totalFaltanOptions}`
                      : buttonText}
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};
